import { type DraftTargetLink, type IdentifierRecord } from '@myplayer/schema';
import {
  IconCheck,
  IconClock,
  IconEdit,
  IconExternalLink,
  IconPlus,
  IconTrash,
  IconUndo,
} from '@myplayer/ui';
import { type FormEvent, type ReactElement } from 'react';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  Tag,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildTargetLinkContextMenuItems } from './context-menu-items.js';
import { ProductLinkEditorModal } from './product-links-editor.js';
import {
  formatPlainLinkTypeLabel,
  resolvePendingFiredScheduledChange,
  resolveScheduledChange,
} from './products-helpers.js';

export interface ProductAdvancedLinksSectionProps {
  readonly selectedRecord?: IdentifierRecord | null | undefined;
  readonly workingLinks: readonly DraftTargetLink[];
  readonly canEdit: boolean;
  readonly savingDraft: boolean;
  readonly isExtraLinkModalOpen: boolean;
  readonly editingIndex: number | null;
  readonly linkType: string;
  readonly setLinkType: (val: string) => void;
  readonly linkTitle: string;
  readonly setLinkTitle: (val: string) => void;
  readonly linkUrl: string;
  readonly setLinkUrl: (val: string) => void;
  readonly linkLanguage: string;
  readonly setLinkLanguage: (val: string) => void;
  readonly linkRegion: string;
  readonly setLinkRegion: (val: string) => void;
  readonly linkIsDefault: boolean;
  readonly setLinkIsDefault: (val: boolean) => void;
  readonly linkFormError: string | null;
  readonly effectiveResolverHost?: string;
  readonly onOpenAddLinkModal: () => void;
  readonly onStartEditLink: (index: number) => void;
  readonly onSetMainLink?: ((index: number) => Promise<unknown>) | undefined;
  readonly onScheduleLink?: ((targetHref: string) => void) | undefined;
  readonly onMarkLinkRemoved: (index: number) => Promise<unknown>;
  readonly onRestoreLink: (index: number) => Promise<unknown>;
  readonly onSaveLinkForm: (e: FormEvent) => Promise<void>;
  readonly onCancelEdit: () => void;
}

export function ProductAdvancedLinksSection({
  selectedRecord,
  workingLinks,
  canEdit,
  savingDraft,
  isExtraLinkModalOpen,
  editingIndex,
  linkType,
  setLinkType,
  linkTitle,
  setLinkTitle,
  linkUrl,
  setLinkUrl,
  linkLanguage,
  setLinkLanguage,
  linkRegion,
  setLinkRegion,
  linkIsDefault,
  setLinkIsDefault,
  linkFormError,
  effectiveResolverHost,
  onOpenAddLinkModal,
  onStartEditLink,
  onSetMainLink,
  onScheduleLink,
  onMarkLinkRemoved,
  onRestoreLink,
  onSaveLinkForm,
  onCancelEdit,
}: ProductAdvancedLinksSectionProps): ReactElement {
  const scheduled = selectedRecord ? resolveScheduledChange(selectedRecord) : null;
  const pendingFired = selectedRecord ? resolvePendingFiredScheduledChange(selectedRecord) : null;
  const activeCount = workingLinks.filter((l) => l.removed !== true).length;
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const indexedLinks = workingLinks
    .map((link, idx) => ({ link, idx }))
    .sort((a, b) => {
      const aMain = a.link.defaultLink && a.link.removed !== true ? 1 : 0;
      const bMain = b.link.defaultLink && b.link.removed !== true ? 1 : 0;
      if (aMain !== bMain) return bMain - aMain;
      return a.idx - b.idx;
    });

  return (
    <>
      <Card data-testid="product-extra-links-card">
        <CardHeader>
          <div className="flex min-w-0 items-center gap-3">
            <CardIcon>
              <IconExternalLink />
            </CardIcon>
            <div className="min-w-0">
              <CardTitle>Product links ({activeCount})</CardTitle>
              <CardDescription>
                Manage destination links by GS1 link type, language, and region. One link is set as
                the main scan destination.
              </CardDescription>
            </div>
          </div>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              data-testid="add-extra-link-button"
              onClick={onOpenAddLinkModal}
            >
              <IconPlus />
              <span>Add link</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {workingLinks.length === 0 ? (
            <p className="py-2 text-[12px] text-fg-secondary">No links configured yet.</p>
          ) : (
            <ul
              aria-label="Target links list"
              className="divide-y divide-subtle border-y border-subtle"
            >
              {indexedLinks.map(({ link, idx }) => {
                const isRemoved = link.removed === true;
                const isMain = link.defaultLink && !isRemoved;
                const displayHref =
                  isMain && pendingFired !== null ? pendingFired.targetHref : link.href;
                const isScheduledLink =
                  !isRemoved && scheduled !== null && scheduled.targetHref === link.href;
                const canRemoveLink = !isMain && activeCount > 1;
                return (
                  <li
                    key={`${link.linkType}-${link.href}-${String(idx)}`}
                    data-testid={`draft-link-row-${String(idx)}`}
                    onContextMenu={bindContextMenu(() =>
                      buildTargetLinkContextMenuItems({
                        link,
                        index: idx,
                        canEdit,
                        savingDraft,
                        canRemoveLink,
                        onSetMainLink,
                        onStartEditLink,
                        onScheduleLink,
                        onMarkLinkRemoved,
                        onRestoreLink,
                      }),
                    )}
                    className={`flex flex-wrap items-center justify-between gap-3 py-3 text-[12px] ${
                      isRemoved ? 'bg-status-error-bg px-2.5' : 'bg-surface'
                    }`}
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`font-semibold text-fg ${isRemoved ? 'line-through' : ''}`}
                        >
                          {link.title}
                        </span>
                        <Tag variant="outline">{formatPlainLinkTypeLabel(link.linkType)}</Tag>
                        {isMain && (
                          <Badge variant="success" data-testid="main-link-badge">
                            Main scan destination
                          </Badge>
                        )}
                        {isScheduledLink && (
                          <Badge
                            variant="warning"
                            data-testid={`scheduled-link-badge-${String(idx)}`}
                          >
                            Scheduled for {scheduled.formattedDate}
                          </Badge>
                        )}
                        {isRemoved && (
                          <Badge
                            data-testid={`removed-link-badge-${String(idx)}`}
                            variant="destructive"
                          >
                            Removed
                          </Badge>
                        )}
                      </div>
                      <div className="flex min-w-0 items-center gap-1.5 font-mono text-fg-secondary">
                        {!isRemoved && /^https?:\/\//i.test(displayHref) ? (
                          <a
                            href={displayHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex min-w-0 items-center gap-1.5 truncate underline decoration-subtle underline-offset-2 hover:text-brand"
                          >
                            <IconExternalLink className="shrink-0" />
                            <span className="truncate">{displayHref}</span>
                          </a>
                        ) : (
                          <>
                            <IconExternalLink className="shrink-0" />
                            <span className={`truncate ${isRemoved ? 'line-through' : ''}`}>
                              {displayHref}
                            </span>
                          </>
                        )}
                        <span className="shrink-0 text-[11px] text-fg-muted">
                          ({link.hreflang.join(', ')} · {link.context.join(', ')})
                        </span>
                      </div>
                    </div>

                    {canEdit && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {!isRemoved ? (
                          <>
                            {!isMain && onSetMainLink !== undefined && (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                data-testid={`set-main-link-button-${String(idx)}`}
                                aria-label={`Set ${link.title} as main link`}
                                onClick={() => void onSetMainLink(idx)}
                                disabled={savingDraft}
                              >
                                <IconCheck />
                                <span>Set as main link</span>
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              aria-label={`Edit link ${link.title}`}
                              onClick={() => onStartEditLink(idx)}
                              disabled={savingDraft}
                            >
                              <IconEdit />
                              <span>Edit</span>
                            </Button>
                            {(!isMain && onScheduleLink !== undefined) || canRemoveLink ? (
                              <ActionOverflowMenu
                                label={`More actions for ${link.title}`}
                                data-testid={`link-overflow-menu-${String(idx)}`}
                                disabled={savingDraft}
                              >
                                {!isMain && onScheduleLink !== undefined && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    data-testid={`schedule-this-link-button-${String(idx)}`}
                                    aria-label={`Schedule ${link.title}`}
                                    onClick={() => onScheduleLink(link.href)}
                                    disabled={savingDraft}
                                  >
                                    <IconClock />
                                    <span>Schedule this link</span>
                                  </Button>
                                )}
                                {canRemoveLink && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    aria-label={`Remove link ${link.title}`}
                                    onClick={() => void onMarkLinkRemoved(idx)}
                                    disabled={savingDraft}
                                  >
                                    <IconTrash />
                                    <span>Remove</span>
                                  </Button>
                                )}
                              </ActionOverflowMenu>
                            ) : null}
                          </>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            aria-label={`Restore link ${link.title}`}
                            onClick={() => void onRestoreLink(idx)}
                            disabled={savingDraft}
                          >
                            <IconUndo />
                            <span>Restore</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      {contextMenuElement}

      <ProductLinkEditorModal
        open={isExtraLinkModalOpen}
        editingIndex={editingIndex}
        savingDraft={savingDraft}
        linkType={linkType}
        setLinkType={setLinkType}
        linkTitle={linkTitle}
        setLinkTitle={setLinkTitle}
        linkUrl={linkUrl}
        setLinkUrl={setLinkUrl}
        linkLanguage={linkLanguage}
        setLinkLanguage={setLinkLanguage}
        linkRegion={linkRegion}
        setLinkRegion={setLinkRegion}
        linkIsDefault={linkIsDefault}
        setLinkIsDefault={setLinkIsDefault}
        linkFormError={linkFormError}
        effectiveResolverHost={effectiveResolverHost}
        onSaveLinkForm={onSaveLinkForm}
        onCancelEdit={onCancelEdit}
      />
    </>
  );
}
